'use client'

import { clsx } from 'clsx'
import type { ImageProps as NextImageProps } from 'next/image'
import NextImage from 'next/image'
import { useEffect, useState } from 'react'
import ReactMediumImageZoom, { type UncontrolledProps } from 'react-medium-image-zoom'

export interface ImageProps extends Omit<NextImageProps, 'src' | 'priority'> {
  src: string
  fallbackSrc?: string
  aspectRatio?: string
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
}

// Fallback SVG image (very light, ~500 bytes)
export const defaultFallbackSrc =
  'data:image/svg+xml;base64,' +
  btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
      <rect width="100%" height="100%" fill="#fdf9f4"/>
      <circle cx="200" cy="140" r="40" fill="#896145" />
      <line x1="180" y1="120" x2="220" y2="160" stroke="#fff" stroke-width="6" />
      <line x1="220" y1="120" x2="180" y2="160" stroke="#fff" stroke-width="6" />
      <text x="50%" y="90%" dominant-baseline="middle" text-anchor="middle"
        font-size="16" fill="#896145">Image Not Found</text>
    </svg>
  `)

export function Image(props: ImageProps) {
  const {
    alt = 'image',
    src,
    fallbackSrc,
    loading = 'lazy',
    style,
    className,
    width,
    height,
    aspectRatio = '4 / 3',
    objectFit = 'cover',
    ...rest
  } = props

  const isFixedSize = typeof width === 'number' && typeof height === 'number'

  const [currentSrc, setCurrentSrc] = useState(src)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [failedSrcs, setFailedSrcs] = useState<Set<string>>(new Set())

  const effectiveFallback = fallbackSrc || defaultFallbackSrc

  // Reset state khi src thay đổi
  useEffect(() => {
    if (!src) {
      setCurrentSrc(effectiveFallback)
      setHasError(true)
      setIsLoaded(true)
      return
    }

    // Nếu src này đã lỗi trước đó, dùng fallback ngay
    if (failedSrcs.has(src)) {
      setCurrentSrc(effectiveFallback)
      setHasError(true)
      setIsLoaded(true)
      return
    }

    // Reset state và thử load ảnh mới
    setCurrentSrc(src)
    setHasError(false)
    setIsLoaded(false)

    // Create new image instance to preload
    const img = document.createElement('img')
    img.src = src

    // Handle both load and error events
    const handleLoad = () => setIsLoaded(true)
    const handleError = () => {
      // Đánh dấu src này đã lỗi và chuyển sang fallback
      setFailedSrcs((prev) => new Set(prev).add(src))
      setCurrentSrc(effectiveFallback)
      setHasError(true)
      setIsLoaded(true)
    }

    img.addEventListener('load', handleLoad)
    img.addEventListener('error', handleError)

    // If image is already complete, set loaded state
    if (img.complete) {
      setIsLoaded(true)
    }

    // Force isLoaded to true after 1 second
    const timeoutId = setTimeout(() => {
      setIsLoaded(true)
    }, 1000)

    // Cleanup event listeners and timeout
    return () => {
      img.removeEventListener('load', handleLoad)
      img.removeEventListener('error', handleError)
      clearTimeout(timeoutId)
    }
  }, [src, effectiveFallback])

  const handleErrorImage = () => {
    if (currentSrc !== effectiveFallback && !hasError) {
      setFailedSrcs((prev) => new Set(prev).add(currentSrc))
      setCurrentSrc(effectiveFallback)
      setHasError(true)
      setIsLoaded(true)
    }
  }
  const normalizedSrc = (currentSrc?.trim() || fallbackSrc || defaultFallbackSrc) as string
  return (
    <div
      className={clsx(
        'image-container relative h-full overflow-hidden transition-opacity duration-300',
        className
      )}
      style={{
        ...(aspectRatio && !isFixedSize ? { aspectRatio } : {}),
        ...style,
      }}
    >
      {!isLoaded && (
        <div
          className="absolute inset-0 z-10 animate-pulse bg-[#896145]/10"
          style={{
            ...(isFixedSize ? { width, height } : {}),
            borderRadius: 'inherit',
          }}
        />
      )}

      <NextImage
        {...rest}
        src={normalizedSrc}
        alt={alt ?? 'image'}
        loading={loading}
        priority={loading === 'eager'}
        quality={100}
        onLoad={() => setIsLoaded(true)}
        onError={handleErrorImage}
        className={clsx(
          'transition-opacity duration-700 ease-in-out',
          isLoaded ? 'opacity-100 blur-0' : 'opacity-40 blur-md'
        )}
        style={{ objectFit, ...style }}
        {...(isFixedSize
          ? { width, height }
          : { fill: true, sizes: '(max-width: 768px) 100vw, 50vw' })}
      />
    </div>
  )
}

interface ZoomProps extends UncontrolledProps {
  children: React.ReactNode
}

export function Zoom({ children, classDialog, ...rest }: ZoomProps) {
  return (
    <ReactMediumImageZoom
      zoomMargin={20}
      classDialog={clsx([
        '[&_[data-rmiz-modal-img]]:rounded-lg',
        '[&_[data-rmiz-btn-unzoom]]:hidden',
        '[&_[data-rmiz-modal-overlay="visible"]]:bg-black/80',
        classDialog,
      ])}
      {...rest}
    >
      {children}
    </ReactMediumImageZoom>
  )
}
